/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import { EuiButton, EuiFlexGroup, EuiPageHeader, EuiSpacer } from '@elastic/eui'

import { getCCMServices, isCCMAddNameFeatureActivated } from '@modules/ccm-user-lib/feature'
import {
  useDeleteCCMCluster,
  useGetCCMClusters,
  usePatchCCMCluster,
} from '@modules/ccm-user-lib/hooks'
import { connectClusterWizardURL, connectedClustersURL } from '@modules/ccm-user-lib/urls'
import { adaptFromWebAPI } from '@modules/ccm-user-lib/util'
import Breadcrumbs from '@modules/cui/Breadcrumbs'
import { FormattedMessage } from '@modules/i18n'
import history from '@modules/utils/history'

import ClusterTable from './ClusterTable'

import type { FC } from 'react'

const PAGE_BREADCRUMBS = [
  {
    text: (
      <FormattedMessage
        id='ccm.cluster-list-page.breadcrumbs.title'
        defaultMessage='Connected Clusters'
      />
    ),
    to: connectedClustersURL(),
  },
]

const ClusterListPage: FC = () => {
  const { data, isLoading } = useGetCCMClusters()

  const { mutate: patchCCMCluster, isLoading: isPatching } = usePatchCCMCluster()

  const clusters = adaptFromWebAPI(data)

  const onClusterAddOrUpdateName = (clusterId: string, name: string) => {
    patchCCMCluster({
      id: clusterId,
      body: {
        name,
      },
    })
  }

  const { mutate: deleteCCMCluster, isLoading: isDeleting } = useDeleteCCMCluster()

  const onClusterDisconnect = (clusterId: string) => {
    deleteCCMCluster({ id: clusterId })
  }

  return (
    <Fragment>
      <Breadcrumbs breadcrumbs={PAGE_BREADCRUMBS} />

      <EuiPageHeader
        bottomBorder={true}
        pageTitle={
          <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
            <FormattedMessage
              id='ccm.cluster-list-page.title'
              defaultMessage='Connected Clusters'
            />
            <EuiButton
              fill={true}
              onClick={() => {
                history.push(connectClusterWizardURL())
              }}
            >
              <FormattedMessage
                id='ccm.cluster-list-page.add-cluster-button'
                defaultMessage='Connect Cluster'
              />
            </EuiButton>
          </EuiFlexGroup>
        }
      />
      <EuiFlexGroup direction='column' justifyContent='center' alignItems='center'>
        <EuiSpacer size='xxl' />
        <EuiFlexGroup>
          <ClusterTable
            clusters={clusters}
            availableCcmServices={getCCMServices()}
            isCCMAddNameFeatureActivated={isCCMAddNameFeatureActivated()}
            isLoading={isLoading || isPatching || isDeleting}
            onClusterAddName={onClusterAddOrUpdateName}
            onClusterDisconnect={onClusterDisconnect}
            onClusterUpdateName={onClusterAddOrUpdateName}
          />
        </EuiFlexGroup>
      </EuiFlexGroup>
    </Fragment>
  )
}

export default ClusterListPage
